import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';

import { getUserTypeConfig } from '../../config/UserTypes';
import HeaderFilterSelect from '../common/HeaderFilterSelect';
import { useUsersTableUrlState } from './useUsersTableUrlState';

const sortItems = (items, sortConfig, columns) => {
  const column = columns.find((c) => c.key === sortConfig.key);
  if (!column?.sort) return items;
  const direction = sortConfig.direction === 'asc' ? 1 : -1;
  return [...items].sort((a, b) => column.sort(a, b) * direction);
};

const UsersTable = ({ userType, items }) => {
  const cfg = getUserTypeConfig(userType);
  const navigate = useNavigate();

  // `cfg.table` is a getter in some configs, so it returns a fresh object on
  // every access. Memoize it (and everything derived from it) to keep stable
  // references across renders.
  const table = useMemo(() => cfg.table, [cfg]);
  const columns = table.columns;
  const defaultSort = table.defaultSort;

  const { sortableKeys, filterKeys, filterColumns } = useMemo(() => ({
    sortableKeys: columns.filter((c) => c.sortable).map((c) => c.key),
    filterKeys: columns.filter((c) => c.filter).map((c) => c.key),
    filterColumns: columns.filter((c) => c.filter),
  }), [columns]);

  const {
    filters,
    setFilter,
    sortConfig,
    setSortConfig,
  } = useUsersTableUrlState({
    sortableKeys,
    filterKeys,
    defaultSort,
  });

  const filtered = items.filter((item) =>
    filterColumns.every((col) => col.filter.match(item, filters[col.key]))
  );
  const sorted = sortItems(filtered, sortConfig, columns);

  const handleSort = (key) => {
    const column = columns.find((c) => c.key === key);
    const defaultDir = column?.defaultDir ?? 'asc';
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key
          ? prev.direction === 'asc' ? 'desc' : 'asc'
          : defaultDir,
    }));
  };

  const onRowClick = (id) => {
    navigate(cfg.routes.view(id), { state: { fromFilteredTable: true } });
  };

  const renderCellContent = (col, item) => {
    const content = col.render(item);
    if (!col.tooltip) return content;

    const tooltipText = col.tooltip(item);
    return (
      <Tooltip title={tooltipText} disableHoverListener={!tooltipText}>
        <Typography
          variant="body2"
          sx={{
            maxWidth: col.width,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {content}
        </Typography>
      </Tooltip>
    );
  };

  const renderHeader = (col) => {
    if (col.sortable) {
      return (
        <TableSortLabel
          active={sortConfig.key === col.key}
          direction={sortConfig.direction}
          onClick={() => handleSort(col.key)}
        >
          {col.header}
        </TableSortLabel>
      );
    }
    if (col.filter) {
      const options = col.filter.options.map((option) => ({
        ...option,
        count: items.filter((item) => col.filter.countBy(item) === option.value).length,
      }));
      return (
        <HeaderFilterSelect
          label={col.header}
          value={filters[col.key] ?? ''}
          onChange={(event) => setFilter(col.key, event.target.value)}
          options={options}
          totalCount={items.length}
          minWidth={0}
        />
      );
    }
    return col.header;
  };

  return (
    <Box>
      <TableContainer>
        <Table sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.key} sx={{ width: col.width, maxWidth: col.width }}>
                  {renderHeader(col)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((item) => (
              <TableRow
                key={item.id}
                hover
                onClick={() => onRowClick(item.id)}
                sx={{
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                {columns.map((col) => (
                  <TableCell key={col.key} sx={{ width: col.width, maxWidth: col.width }}>
                    {renderCellContent(col, item)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {sorted.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <Typography variant="body1" color="textSecondary">
                    {cfg.labels.emptyState}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default UsersTable;
